/**
 * Market Pulse API client
 *
 * Uses Next.js API routes (/api/daily-market-pulse, /api/weekly-podcast) as proxy.
 * This ensures same-origin requests from the browser - no CORS.
 * The API routes run server-side and proxy to the backend.
 */

// Relative URLs - same origin, no CORS. API routes proxy to backend.
const DAILY_PULSE_API = '/api/daily-market-pulse';
const WEEKLY_PODCAST_API = '/api/weekly-podcast';

export interface MarketPulseResponse {
  script: string;
  date: string;
  area_name: string;
  mode: 'daily' | 'weekly';
  format?: 'single_voice' | 'two_person_conversation';
  hosts?: string[];
}

/**
 * Parse a conversation script into speaker segments
 * @param script - Script with speaker labels (e.g., "SARAH: Hello\nMIKE: Hi")
 * @returns Array of {speaker, text} objects
 */
export function parseConversationScript(script: string): Array<{ speaker: string; text: string }> {
  const segments: Array<{ speaker: string; text: string }> = [];
  
  // Split by speaker labels (SARAH: or MIKE:)
  const pattern = /(?:^|\n)(SARAH|MIKE):\s*/gi;
  const parts = script.split(pattern);
  
  // Process pairs of (speaker, dialogue)
  let i = 1; // Start from index 1 (after potential leading text)
  while (i < parts.length - 1) {
    const speaker = parts[i].trim().toUpperCase();
    const text = parts[i + 1]?.trim() || '';
    
    if ((speaker === 'SARAH' || speaker === 'MIKE') && text) {
      segments.push({ speaker, text });
    }
    i += 2;
  }
  
  return segments;
}

/**
 * Fetch Daily Market Pulse script (via Next.js API proxy - same origin, no CORS)
 */
export async function fetchDailyPulse(date?: string, areaName: string = 'Dubai'): Promise<MarketPulseResponse> {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  params.set('area_name', areaName);
  const url = `${DAILY_PULSE_API}?${params.toString()}`;

  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || err?.detail || `Failed to fetch daily pulse: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch Weekly Market Podcast script (via Next.js API proxy - same origin, no CORS)
 */
export async function fetchWeeklyPodcast(endDate?: string, areaName: string = 'Dubai'): Promise<MarketPulseResponse> {
  const params = new URLSearchParams();
  if (endDate) params.set('end_date', endDate);
  params.set('area_name', areaName);
  const url = `${WEEKLY_PODCAST_API}?${params.toString()}`;

  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || err?.detail || `Failed to fetch weekly podcast: ${response.statusText}`);
  }

  return response.json();
}
